import type { LoaderArgs, PageArgs } from "bunfx";
import { RedirectError } from "bunfx";
import {
  IcoChevronRight,
  IcoPlus,
  IcoUser,
  IcoUsers,
} from "../../components/icons";
import { BrandedLayout } from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import type { MimicState } from "../../stores/mimic";
import type {
  BrandColor,
  MemberRole,
  OrganizationDetails,
  OrgMember,
  User,
} from "../../types";
import { isAdmin } from "../../types";

type MembersPageState = {
  currentUser: User;
  mimic: MimicState;
  organization: OrganizationDetails;
  logoUrl: string | null;
  brandColor: BrandColor;
  currentMemberId: string;
  hasReports: boolean;
};

export async function load(args: LoaderArgs): Promise<MembersPageState> {
  const orgId = args.params.orgId ?? "";

  const [authResult, orgsResult, orgResult] = await Promise.all([
    rpc.auth.me({}),
    rpc.orgs.list({}),
    rpc.orgs.get({ id: orgId }),
  ]);

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  // Only admins can view member list
  if (!isAdmin(orgResult.currentMember.role)) {
    throw new RedirectError("/");
  }

  // Get brand color from list endpoint
  const orgFromList = orgsResult.organizations.find((o) => o.id === orgId);
  const brandColor = (orgFromList?.brandColor ?? "indigo") as BrandColor;

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    organization: orgResult.organization,
    logoUrl: orgResult.organization.logoUrl ?? null,
    brandColor,
    currentMemberId: orgResult.currentMember.id,
    hasReports: orgFromList?.hasReports ?? false,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Members");
  const { currentUser, mimic, organization, brandColor, hasReports } =
    state.value;

  // Separate members into categories
  const activeMembers = organization.members.filter(
    (m) => m.acceptedAt && !m.archivedAt && !m.leftAt,
  );
  const pendingMembers = organization.members.filter(
    (m) => !m.acceptedAt && !m.archivedAt && !m.leftAt,
  );
  const archivedMembers = organization.members.filter(
    (m) => m.archivedAt || m.leftAt,
  );

  return (
    <BrandedLayout
      user={currentUser}
      mimic={mimic}
      orgId={organization.id}
      orgName={organization.name}
      brandColor={brandColor}
      activeTab="settings"
      isAdmin={true}
      formCount={organization.formCount}
      hasReports={hasReports}
    >
      {/* Section header with invite button */}
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold text-text">Members</h1>
          <p class="text-text-muted mt-1">
            Manage who has access to {organization.name}
          </p>
        </div>
        <a
          href={`/orgs/${organization.id}/invite`}
          class="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium bg-[var(--brand-600)] hover:bg-[var(--brand-700)] text-white transition-colors"
        >
          <IcoPlus class="size-4" />
          Invite Members
        </a>
      </div>

      {/* Active Members */}
      <MemberSection
        title="Members"
        icon={<IcoUsers class="size-4" />}
        members={activeMembers}
        currentUserId={currentUser.id}
        orgId={organization.id}
      />

      {/* Pending Invitations */}
      {pendingMembers.length > 0 && (
        <MemberSection
          title="Pending Invitations"
          members={pendingMembers}
          currentUserId={currentUser.id}
          orgId={organization.id}
          isPending
        />
      )}

      {/* Archived Members */}
      {archivedMembers.length > 0 && (
        <MemberSection
          title="Archived"
          members={archivedMembers}
          currentUserId={currentUser.id}
          orgId={organization.id}
          isArchived
        />
      )}
    </BrandedLayout>
  );
}

function MemberSection({
  title,
  icon,
  members,
  currentUserId,
  orgId,
  isPending,
  isArchived,
}: {
  title: string;
  icon?: preact.JSX.Element;
  members: OrgMember[];
  currentUserId: string;
  orgId: string;
  isPending?: boolean;
  isArchived?: boolean;
}) {
  if (members.length === 0) return null;

  return (
    <section aria-label={title} class="mb-6">
      <h2 class="flex items-center gap-2 text-sm font-semibold text-text-muted uppercase tracking-wide mb-3">
        {icon}
        {title}
        <span class="font-normal">({members.length})</span>
      </h2>
      <div class="rounded-xl border border-border bg-surface overflow-hidden divide-y divide-border">
        {members.map((member) => (
          <MemberRow
            key={member.id}
            member={member}
            isCurrentUser={member.userId === currentUserId}
            orgId={orgId}
            isPending={isPending}
            isArchived={isArchived}
          />
        ))}
      </div>
    </section>
  );
}

function MemberRow({
  member,
  isCurrentUser,
  orgId,
  isPending,
  isArchived,
}: {
  member: OrgMember;
  isCurrentUser: boolean;
  orgId: string;
  isPending?: boolean;
  isArchived?: boolean;
}) {
  const displayName = member.userName || member.userEmail;
  const hasName = Boolean(member.userName);
  const isMuted = isPending || isArchived;
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <a
      href={`/orgs/${orgId}/admin/members/${member.id}`}
      class="group flex items-center gap-4 px-5 py-4 hover:bg-surface-hover transition-colors"
    >
      <div
        class={`size-10 rounded-full flex items-center justify-center ring-2 flex-shrink-0 overflow-hidden ${
          isMuted
            ? "bg-surface-muted text-text-muted ring-border"
            : "bg-[var(--brand-100)] text-[var(--brand-600)] ring-[var(--brand-200)]"
        }`}
      >
        {member.userProfilePictureUrl ? (
          <img
            src={member.userProfilePictureUrl}
            alt={displayName}
            class="size-full object-cover"
          />
        ) : hasName ? (
          <span class="text-sm font-semibold">{initial}</span>
        ) : (
          <IcoUser class="size-5" />
        )}
      </div>
      <div class="flex flex-col min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <span
            class={`font-medium truncate ${isMuted ? "text-text-muted" : "text-text group-hover:text-[var(--brand-600)]"}`}
          >
            {displayName}
          </span>
          {isCurrentUser && <span class="text-xs text-text-muted">(you)</span>}
          <RoleBadge
            role={member.role}
            isPending={isPending}
            isArchived={isArchived}
            hasLeft={Boolean(member.leftAt)}
          />
        </div>
        {hasName && (
          <span class="text-sm text-text-muted truncate">
            {member.userEmail}
          </span>
        )}
      </div>
      <IcoChevronRight class="size-5 text-text-muted group-hover:text-[var(--brand-500)] transition-colors flex-shrink-0" />
    </a>
  );
}

function RoleBadge({
  role,
  isPending,
  isArchived,
  hasLeft,
}: {
  role: MemberRole;
  isPending?: boolean;
  isArchived?: boolean;
  hasLeft?: boolean;
}) {
  if (isPending) {
    return (
      <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
        Pending
      </span>
    );
  }

  if (isArchived) {
    return (
      <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-surface-muted text-text-muted">
        {hasLeft ? "Left" : "Archived"}
      </span>
    );
  }

  if (role === "admin") {
    return (
      <span class="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-300">
        Admin
      </span>
    );
  }

  return null;
}
